import { formatDurationLabel } from '@barbearia/shared';

/**
 * Formatação para leitura humana, em pt-BR e no fuso da barbearia.
 *
 * Todo instante vem da API em UTC. Se alguma tela formatar sem passar o fuso,
 * o barbeiro de Porto Alegre vai ver a agenda de Londres — por isso o fuso é
 * parâmetro obrigatório em tudo que mostra hora.
 */

export function timeOf(instant: string | Date, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(instant));
}

export function dayOf(instant: string | Date, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone,
  }).format(new Date(instant));
}

/**
 * "11/08/2026" — com ano, para o que pode ser antigo.
 *
 * A agenda vive na semana e dispensa o ano; a lista de aparelhos, não: duas
 * passkeys registradas em agostos diferentes ficariam idênticas na tela, e é
 * justamente qual delas é a velha que se quer saber ali.
 */
export function fullDateOf(instant: string | Date, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: timezone,
  }).format(new Date(instant));
}

export function weekdayOf(instant: string | Date, timezone: string): string {
  return new Intl.DateTimeFormat('pt-BR', { weekday: 'short', timeZone: timezone })
    .format(new Date(instant))
    .replace('.', '');
}

/** "quarta-feira, 05 de agosto" — o cabeçalho da agenda. */
export function longDateOf(calendarDate: string, timezone: string): string {
  const label = new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    timeZone: timezone,
  }).format(new Date(`${calendarDate}T12:00:00Z`));

  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * "14 de julho" — aniversário, que é data de calendário e não instante.
 *
 * Não recebe fuso de propósito: a data já é local por definição, e passá-la por
 * uma conversão de fuso é justamente o que faria o aniversário de quem nasceu
 * dia 14 aparecer como dia 13.
 */
export function dayAndMonth(calendarDate: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${calendarDate}T12:00:00Z`));
}

/**
 * "13 de agosto de 2026" — data de calendário com ano, para o que envelhece.
 *
 * Mesmo cuidado do aniversário: sem fuso no caminho, senão a novidade publicada
 * dia 13 aparece como dia 12 para quem estiver a oeste. O ano entra porque a
 * lista de novidades é acumulativa — daqui a um ano, "13 de agosto" sozinho não
 * diz qual.
 */
export function dayMonthAndYear(calendarDate: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${calendarDate}T12:00:00Z`));
}

export function money(cents: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (cents ?? 0) / 100,
  );
}

/**
 * "55min", "1h10".
 *
 * A conta mora em `@barbearia/shared` porque o servidor escreve as mesmas
 * frases — a mensagem de "não cabe" e o texto do WhatsApp precisam dizer o
 * tempo igual ao que está na tela ao lado.
 */
export function duration(minutes: number): string {
  return formatDurationLabel(minutes);
}

export function phone(e164: string): string {
  const digits = e164.replace(/\D+/g, '');
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    const area = digits.slice(2, 4);
    const rest = digits.slice(4);
    const split = rest.length === 9 ? 5 : 4;
    return `(${area}) ${rest.slice(0, split)}-${rest.slice(split)}`;
  }
  return e164;
}

/** "hoje", "amanhã" ou a data — o que o barbeiro leria em voz alta. */
export function relativeDay(calendarDate: string, todayDate: string, timezone: string): string {
  if (calendarDate === todayDate) return 'Hoje';

  const day = new Date(`${calendarDate}T12:00:00Z`);
  const today = new Date(`${todayDate}T12:00:00Z`);
  const diff = Math.round((day.getTime() - today.getTime()) / 86_400_000);

  if (diff === 1) return 'Amanhã';
  if (diff === -1) return 'Ontem';

  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    timeZone: timezone,
  })
    .format(day)
    .replace('.', '');
}

/** Data de calendário de hoje no fuso da barbearia. */
export function todayIn(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).format(new Date());
  return parts;
}

export function addDays(calendarDate: string, days: number): string {
  const date = new Date(`${calendarDate}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Minutos desde a meia-noite local — a linha do tempo posiciona por isso. */
export function minutesOfDay(instant: string | Date, timezone: string): number {
  const [hours, minutes] = timeOf(instant, timezone).split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function parseHhMm(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}
